import type { TFunction } from 'i18next';
import type { ApprovalAttributes } from '@/types/approval';

/** 거래처 신규/변경 Business Partner Registration — 레거시 appr_edit_forms_eas_hk_bp·eas_tw_bp(금액 구간·통화만 다름) */
export const BP_HK_TYPE = 200;
export const BP_TW_TYPE = 201;
/** 회계거래처 신규/변경 — 업체소싱정보 표만 같고 SAP 진입·저장은 accountingBusinessPartner */
export const ACCOUNTING_BP_TYPE = 101;

export type BpType = typeof BP_HK_TYPE | typeof BP_TW_TYPE;
/** 업체소싱정보 표를 쓰는 양식 */
export type BpFormType = BpType | typeof ACCOUNTING_BP_TYPE;

export type BpRequestType = 'register' | 'update';
export type BpSelectionMethod = '' | 'bidding' | 'direct';
export type BpSourcingPath = '' | 'employee' | 'partner' | 'internet' | 'other';
export type BpInterest = '' | 'yes' | 'no';

export const BP_REQUEST_TYPES: { value: BpRequestType; label: string }[] = [
  { value: 'register', label: '거래처 신청 유형 생성' },
  { value: 'update', label: '거래처 신청 유형 변경' },
];

export const BP_SELECTION_METHOD_LABEL: Record<
  Exclude<BpSelectionMethod, ''>,
  string
> = {
  bidding: '경쟁입찰방식',
  direct: '수의계약방식',
};

export const BP_CYCLES = [
  { value: 'oneTime', label: '일회성' },
  { value: 'yearly1to2', label: '연 1~2회' },
  { value: 'yearly3to6', label: '연 3~6회' },
  { value: 'yearly6to12', label: '연 6~12회' },
  { value: 'ongoing', label: '상시' },
] as const;

export type BpCycle = '' | (typeof BP_CYCLES)[number]['value'];

/** 이해관계 상세 행·표 머리글 — 101 레거시만 문구가 다름 */
export const bpInterestDetailLabels = (apvType: BpFormType) =>
  apvType === ACCOUNTING_BP_TYPE
    ? {
        note: '(이해관계 있을경우)',
        contentHeader: '이해관계 내용',
      }
    : { note: null, contentHeader: '이해관계 유형' };

const BP_HK_AMOUNTS = [
  { value: 'lt30k', prefix: '미만', range: '30,000' },
  { value: '30kTo200k', range: '30,000 ~ 200,000' },
  { value: '200kTo300k', range: '200,000 ~ 300,000' },
  { value: '300kTo600k', range: '300,000 ~ 600,000' },
  { value: 'gte600k', prefix: '이상', range: '600,000' },
] as const;

const BP_TW_AMOUNTS = [
  { value: 'lt120k', prefix: '미만', range: '120,000' },
  { value: '120kTo800k', range: '120,000 ~ 800,000' },
  { value: '800kTo1200k', range: '800,000 ~ 1,200,000' },
  { value: '1200kTo2400k', range: '1,200,000 ~ 2,400,000' },
  { value: 'gte2400k', prefix: '이상', range: '2,400,000' },
] as const;

const BP_KR_AMOUNTS = [
  { value: 'lt5m', label: '500만원 미만' },
  { value: '5mTo30m', label: '500만원~3000만원 미만' },
  { value: '30mTo50m', label: '3000만원~5000만원 미만' },
  { value: '50mTo100m', label: '5000만원~1억 미만' },
  { value: 'gte100m', label: '1억 이상' },
] as const;

export type BpAmountOption =
  | (typeof BP_HK_AMOUNTS)[number]
  | (typeof BP_TW_AMOUNTS)[number]
  | (typeof BP_KR_AMOUNTS)[number];

export type BpAmount = '' | BpAmountOption['value'];

export type BpAmountSpec = {
  currency: string;
  options: readonly BpAmountOption[];
};

// 양식을 BpFormType 에 추가하면 여기 금액 구간이 없을 때 타입 오류
const BP_AMOUNT_SPECS: Record<BpFormType, BpAmountSpec> = {
  [BP_HK_TYPE]: { currency: 'HKD', options: BP_HK_AMOUNTS },
  [BP_TW_TYPE]: { currency: 'TWD', options: BP_TW_AMOUNTS },
  [ACCOUNTING_BP_TYPE]: { currency: '', options: BP_KR_AMOUNTS },
};

export const isBpRegistrationType = (apvType: number): apvType is BpType =>
  apvType === BP_HK_TYPE || apvType === BP_TW_TYPE;

export const bpAmountSpec = (apvType: BpFormType): BpAmountSpec =>
  BP_AMOUNT_SPECS[apvType];

export const BP_SOURCING_PATH_LABEL: Record<
  Exclude<BpSourcingPath, ''>,
  string
> = {
  employee: '내부 임직원 추천',
  partner: '협력사 추천',
  internet: '인터넷 서칭',
  other: '기타',
};

export const BP_RELATIONS = [
  { value: 'family', label: '혈연' },
  { value: 'alumni', label: '학연' },
  { value: 'regional', label: '지연' },
  { value: 'formerColleague', label: '전직장 동료' },
  { value: 'formerPartner', label: '전직장 거래처' },
  { value: 'employee', label: '자사 전현직 임직원' },
] as const;

export type BpRelation = (typeof BP_RELATIONS)[number]['value'];

export type BpParticipant = {
  id: string;
  department: string;
  name: string;
  position: string;
  checked: boolean;
  stakeholderName: string;
  stakeholderPosition: string;
  relation: BpRelation;
  remark: string;
};

export type BpFormState = {
  requestType: BpRequestType;
  selectionMethod: BpSelectionMethod;
  biddingVendors: string;
  selectionReason: string;
  cycle: BpCycle;
  amount: BpAmount;
  sourcingPath: BpSourcingPath;
  sourcingEmployee: string;
  sourcingPartner: string;
  sourcingOther: string;
  participants: BpParticipant[];
  interest: BpInterest;
};

export const createBpState = (): BpFormState => ({
  requestType: 'register',
  selectionMethod: '',
  biddingVendors: '',
  selectionReason: '',
  cycle: '',
  amount: '',
  sourcingPath: '',
  sourcingEmployee: '',
  sourcingPartner: '',
  sourcingOther: '',
  participants: [],
  interest: '',
});

export const createBpParticipant = (
  person: Pick<BpParticipant, 'id' | 'department' | 'name' | 'position'>,
): BpParticipant => ({
  ...person,
  checked: false,
  stakeholderName: '',
  stakeholderPosition: '',
  relation: BP_RELATIONS[0].value,
  remark: '',
});

export const BP_DUPLICATE_PARTICIPANT_MESSAGE = '이미 등록된 임직원입니다.';

/** 레거시처럼 마지막 참여자를 지우면 이해관계 여부는 '없음'으로 */
export const removeBpParticipant = (
  state: BpFormState,
  id: string,
): BpFormState => {
  const participants = state.participants.filter((p) => p.id !== id);
  return {
    ...state,
    participants,
    interest: participants.length ? state.interest : 'no',
  };
};

export const bpAmountLabel = (
  amount: BpAmountOption,
  currency: string,
  t: TFunction,
): string => {
  if ('label' in amount) return t(amount.label);
  return 'prefix' in amount
    ? `${t(amount.prefix)} ${amount.range} ${currency}`
    : `${amount.range} ${currency}`;
};

export const bpRelationLabel = (relation: BpRelation, t: TFunction): string =>
  t(BP_RELATIONS.find((r) => r.value === relation)?.label ?? '');

const isRegister = (state: BpFormState) => state.requestType === 'register';

/** 레거시 save_doc_eas_hk_bp·eas_tw_bp 검사 순서 그대로(임시저장도 같은 검사). 반환값은 번역 key */
export const checkBpBeforeSave = (
  state: BpFormState | null | undefined,
): string | null => {
  if (!state || !isRegister(state)) return null;
  if (!state.selectionMethod) return '업체선정 방식을 체크하세요.';
  if (state.selectionMethod === 'bidding' && state.biddingVendors === '') {
    return '참여 업체명을 입력하세요.';
  }
  if (!state.selectionReason) return '업체 선정 사유를 입력하세요.';
  if (!state.cycle) return '예상 거래 주기를 체크하세요.';
  if (!state.amount) return '예상 거래 금액을 체크하세요.';
  if (!state.sourcingPath) return '업체 소싱 경로를 체크하세요.';
  if (state.sourcingPath === 'employee' && !state.sourcingEmployee) {
    return '추천 임직원 이름을 입력하세요.';
  }
  if (state.sourcingPath === 'partner' && !state.sourcingPartner) {
    return '협력사 정보를 입력하세요.';
  }
  if (state.sourcingPath === 'other' && !state.sourcingOther) {
    return '기타 경로를 입력하세요.';
  }
  if (!state.participants.length) return '참여자 정보를 입력하세요.';
  if (!state.interest) return '이해관계 여부를 체크하세요.';
  if (state.interest === 'yes') {
    const checked = state.participants.filter((p) => p.checked);
    if (!checked.length) return '이해관계 정보는 1개 이상 선택해야 합니다.';
    if (checked.some((p) => !p.stakeholderName || !p.stakeholderPosition)) {
      return '이해관계자 상세 정보를 전부 입력해주세요.';
    }
  }
  return null;
};

/** 선택 안 된 쪽 입력칸·체크 안 된 이해관계 행 값은 버림 */
export const normalizeBpState = (state: BpFormState): BpFormState => {
  if (!isRegister(state)) return { ...createBpState(), requestType: 'update' };
  const showDetail = state.interest === 'yes';
  return {
    ...state,
    biddingVendors:
      state.selectionMethod === 'bidding' ? state.biddingVendors : '',
    sourcingEmployee:
      state.sourcingPath === 'employee' ? state.sourcingEmployee : '',
    sourcingPartner:
      state.sourcingPath === 'partner' ? state.sourcingPartner : '',
    sourcingOther: state.sourcingPath === 'other' ? state.sourcingOther : '',
    participants: state.participants.map((p) =>
      showDetail && p.checked
        ? p
        : {
            ...p,
            checked: false,
            stakeholderName: '',
            stakeholderPosition: '',
            relation: BP_RELATIONS[0].value,
            remark: '',
          },
    ),
  };
};

/** 레거시 companyData(CL02) — 키·값(작성자 언어 문구) 그대로 + 편집 복원용 bpForm */
export type BpData = Record<string, unknown> & { bpForm?: BpFormState };

export const toBpData = (
  state: BpFormState,
  apvType: BpFormType,
  t: TFunction,
): BpData => {
  if (!isRegister(state)) return { bpForm: state };
  const data: BpData = {};
  if (state.selectionMethod) {
    data.radioHow = t(BP_SELECTION_METHOD_LABEL[state.selectionMethod]);
  }
  if (state.selectionMethod === 'bidding') {
    data.selectionMethodWrite = state.biddingVendors;
  }
  data.selectionReason = state.selectionReason;
  const cycle = BP_CYCLES.find((c) => c.value === state.cycle);
  if (cycle) data.radioCycle = t(cycle.label);
  const { currency, options } = bpAmountSpec(apvType);
  const amount = options.find((a) => a.value === state.amount);
  if (amount) data.radioAmount = bpAmountLabel(amount, currency, t);
  if (state.sourcingPath) {
    data.radioPath = t(BP_SOURCING_PATH_LABEL[state.sourcingPath]);
  }
  if (state.sourcingPath === 'employee') {
    data.radioPath01Input = state.sourcingEmployee;
  }
  if (state.sourcingPath === 'partner') {
    data.radioPath02Input = state.sourcingPartner;
  }
  if (state.sourcingPath === 'other') {
    data.radioPath04Input = state.sourcingOther;
  }
  if (state.interest) data.radioInterests = state.interest;
  data.companyPeople = JSON.stringify(
    state.participants.map((p) => ({
      '임직원 소속': p.department,
      '임직원 성명': p.name,
      '임직원 직급': p.position,
      '이해관계자 성명': p.stakeholderName,
      '이해관계자 직함': p.stakeholderPosition,
      '이해관계자 관계': p.checked ? bpRelationLabel(p.relation, t) : '',
      '이해관계자 비고': p.remark,
    })),
  );
  data.bpForm = state;
  return data;
};

export const buildBpAttributes = (
  state: BpFormState | null | undefined,
  content: string,
  apvType: BpType,
  t: TFunction,
): ApprovalAttributes | null =>
  state ? { content, data: toBpData(state, apvType, t) } : null;

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

const pick = <T extends string>(v: unknown, allowed: readonly T[]): T | '' =>
  allowed.includes(v as T) ? (v as T) : '';

const parseParticipant = (raw: unknown): BpParticipant | null => {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  if (!str(p.id)) return null;
  return {
    id: str(p.id),
    department: str(p.department),
    name: str(p.name),
    position: str(p.position),
    checked: p.checked === true,
    stakeholderName: str(p.stakeholderName),
    stakeholderPosition: str(p.stakeholderPosition),
    relation:
      pick(
        p.relation,
        BP_RELATIONS.map((r) => r.value),
      ) || BP_RELATIONS[0].value,
    remark: str(p.remark),
  };
};

const readData = (
  attributes: unknown,
  key: 'data' | 'companyData',
): Record<string, unknown> | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  let raw = (attributes as Record<string, unknown>)[key];
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw && typeof raw === 'object'
    ? (raw as Record<string, unknown>)
    : null;
};

/** 편집 진입 — V2 저장분(bpForm)만 복원. 레거시 문서는 빈 폼(레거시도 복원 안 함). 101 은 companyData 에 */
export const parseBpFormState = (
  attributes: unknown,
  apvType: BpFormType,
): BpFormState | null => {
  const form = readData(
    attributes,
    apvType === ACCOUNTING_BP_TYPE ? 'companyData' : 'data',
  )?.bpForm;
  if (!form || typeof form !== 'object') return null;
  const f = form as Record<string, unknown>;
  const base = createBpState();
  return {
    requestType: f.requestType === 'update' ? 'update' : 'register',
    selectionMethod: pick(f.selectionMethod, ['bidding', 'direct'] as const),
    biddingVendors: str(f.biddingVendors),
    selectionReason: str(f.selectionReason),
    cycle: pick(
      f.cycle,
      BP_CYCLES.map((c) => c.value),
    ),
    amount: pick(
      f.amount,
      bpAmountSpec(apvType).options.map((a) => a.value),
    ),
    sourcingPath: pick(f.sourcingPath, [
      'employee',
      'partner',
      'internet',
      'other',
    ] as const),
    sourcingEmployee: str(f.sourcingEmployee),
    sourcingPartner: str(f.sourcingPartner),
    sourcingOther: str(f.sourcingOther),
    participants: Array.isArray(f.participants)
      ? f.participants
          .map(parseParticipant)
          .filter((p): p is BpParticipant => !!p)
      : base.participants,
    interest: pick(f.interest, ['yes', 'no'] as const),
  };
};

export const parseBpContent = (attributes: unknown): string | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  const raw = (attributes as Record<string, unknown>).content;
  return typeof raw === 'string' && raw.trim() ? raw : null;
};
